import { escapeHtml } from "./escape.js";
const DEFAULT_BRAND = "#0F7B4A"; // verde farmácia neutro
/** Verde fixo do WhatsApp — todo CTA usa ele, independente da cor da marca. */
const WA = "#03a14a";
const WA_DARK = "#02843d";
function parseHex(hex) {
    const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m)
        return null;
    const n = parseInt(m[1], 16);
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}
function toHex([r, g, b]) {
    const c = (v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0");
    return `#${c(r)}${c(g)}${c(b)}`;
}
/** Mistura com branco. `t` = 0 (cor pura) … 1 (branco). */
function tint(rgb, t) {
    return rgb.map((v) => v + (255 - v) * t);
}
/** Mistura com preto. `t` = 0 (cor pura) … 1 (preto). */
function shade(rgb, t) {
    return rgb.map((v) => v * (1 - t));
}
/** `rgba(r,g,b,a)` a partir de um RGB — sempre válido, em qualquer navegador. */
function rgba(c, alpha) {
    const [r, g, b] = snap(c);
    return `rgba(${r},${g},${b},${alpha})`;
}
/** Arredonda e satura em 0–255 — o mesmo que `toHex` faz na hora de escrever. */
function snap(rgb) {
    return rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
}
function relLuminance([r, g, b]) {
    const f = (v) => {
        const s = v / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
}
function contrast(a, b) {
    const [hi, lo] = [relLuminance(a), relLuminance(b)].sort((x, y) => y - x);
    return (hi + 0.05) / (lo + 0.05);
}
const WHITE = [255, 255, 255];
/**
 * Escurece a marca até ela passar de `min:1` sobre branco. Sem isso, uma marca
 * clara (amarelo, lima) cadastrada pelo suporte deixaria títulos e links
 * ilegíveis — a cor vem de fora, então a legibilidade não pode depender dela.
 */
function readableOnWhite(rgb, min = 4.5) {
    // O contraste é medido já no valor arredondado que vai virar hex — conferir
    // antes do arredondamento deixava passar cores que na página davam 4,48:1.
    let out = snap(rgb);
    for (let i = 0; i < 24 && contrast(out, WHITE) < min; i++)
        out = snap(shade(rgb, 0.06 * (i + 1)));
    return out;
}
/**
 * Fundo do chip do WhatsApp: 14% do verde sobre branco. Resolvido aqui, e não
 * com `color-mix()` no CSS, pelo mesmo motivo do cabeçalho — a função tem
 * `var()` dentro e vira `unset` em navegador sem suporte, apagando o fundo.
 */
const WA_CHIP = toHex(tint(parseHex(WA), 0.86));
export function renderStyles(brandColor) {
    const brand = parseHex(brandColor ?? "") ?? parseHex(DEFAULT_BRAND);
    const brandInk = readableOnWhite(brand);
    const v = {
        brand: toHex(brand),
        brandInk: toHex(brandInk),
        brandDeep: toHex(shade(brandInk, 0.22)),
        // Neutros tingidos com a marca — mantêm a página coerente com qualquer cor.
        bg: toHex(tint(brand, 0.985)),
        // Fundo translúcido do cabeçalho já resolvido aqui. Usar `color-mix()` no
        // CSS deixava o header transparente em Chrome/Edge ≤110 e Firefox ≤112: a
        // declaração tem `var()` dentro, então falha só no valor computado e vira
        // `unset` em vez de cair na declaração anterior.
        headerBg: rgba(tint(brand, 0.985), 0.88),
        surface: toHex(tint(brand, 0.945)),
        chip: toHex(tint(brand, 0.88)),
        line: toHex(tint(brand, 0.87)),
        ink: toHex(shade(tint(brand, 0.1), 0.88)),
        muted: toHex(shade(tint(brand, 0.42), 0.52)),
    };
    const e = (s) => escapeHtml(s);
    return `<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&amp;family=Poppins:wght@600;700;800&amp;display=swap" />
<meta name="theme-color" content="${e(v.brand)}" />
<style>
:root{
  --brand:${e(v.brand)};--brand-ink:${e(v.brandInk)};--brand-deep:${e(v.brandDeep)};
  --wa:${WA};--wa-dark:${WA_DARK};
  --bg:${e(v.bg)};--header-bg:${e(v.headerBg)};--surface:${e(v.surface)};--chip:${e(v.chip)};--line:${e(v.line)};
  --ink:${e(v.ink)};--muted:${e(v.muted)};
  --sans:"Inter",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;
  --display:"Poppins",var(--sans);
  --r:16px;--r-lg:26px;
  --shadow:0 1px 2px rgba(20,10,10,.04),0 8px 24px rgba(20,10,10,.06);
  --shadow-lg:0 12px 40px rgba(20,10,10,.12);
  --wrap:1200px;
}
*,*::before,*::after{box-sizing:border-box}
html{-webkit-text-size-adjust:100%;scroll-behavior:smooth;scroll-padding-top:88px}
body{margin:0;font-family:var(--sans);color:var(--ink);background:var(--bg);line-height:1.6;font-size:16px;-webkit-font-smoothing:antialiased}
img{max-width:100%;height:auto;display:block}
svg{width:1em;height:1em;fill:currentColor;flex:0 0 auto}
h1,h2,h3{font-family:var(--display);line-height:1.12;letter-spacing:-.02em;margin:0}
p{margin:0}
a{color:var(--brand-ink)}
:focus-visible{outline:3px solid var(--brand-ink);outline-offset:3px;border-radius:6px}
.container{width:100%;max-width:var(--wrap);margin:0 auto;padding:0 20px}
.visually-hidden{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}

/* ---------- botões ---------- */
.btn{display:inline-flex;align-items:center;justify-content:center;gap:9px;font-family:var(--sans);font-weight:600;font-size:1rem;line-height:1;text-decoration:none;padding:15px 26px;border-radius:999px;border:1.5px solid transparent;white-space:nowrap;transition:background-color .15s,color .15s,border-color .15s}
.btn svg{font-size:1.15rem}
.btn--wa{background:var(--wa);color:#fff;box-shadow:0 6px 18px rgba(3,161,74,.28)}
.btn--wa:hover{background:var(--wa-dark)}
.btn--ghost{background:#fff;color:var(--ink);border-color:var(--line)}
.btn--ghost:hover{border-color:var(--brand-ink);color:var(--brand-ink)}
.btn--on-brand{background:#fff;color:var(--brand-deep)}
.btn--on-brand:hover{opacity:.88}
.btn--sm{padding:11px 20px;font-size:.94rem}

/* ---------- cabeçalho fixo ---------- */
.site-header{position:sticky;top:0;z-index:40;background:var(--header-bg);-webkit-backdrop-filter:saturate(140%) blur(10px);backdrop-filter:saturate(140%) blur(10px);border-bottom:1px solid var(--line)}
/* Sem desfoque (Safari <18 sem o prefixo, navegadores antigos), o fundo vira opaco. */
@supports not ((backdrop-filter:blur(4px)) or (-webkit-backdrop-filter:blur(4px))){.site-header{background:var(--bg)}}
.site-header .container{display:flex;align-items:center;gap:18px;min-height:68px;padding-block:10px}
.brand{display:flex;align-items:center;gap:10px;text-decoration:none;color:var(--ink);margin-right:auto;min-width:0}
.brand img{max-height:38px;width:auto;object-fit:contain}
.brand span{font-family:var(--display);font-weight:700;font-size:1.05rem;letter-spacing:-.01em;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.nav{display:none;gap:26px}
.nav a{color:var(--muted);text-decoration:none;font-size:.95rem;font-weight:500}
.nav a:hover{color:var(--brand-ink)}
.site-header .btn{display:none}

/* ---------- hero ---------- */
.hero{padding:40px 0 8px}
.hero-grid{display:grid;grid-template-columns:1fr;gap:36px;align-items:center}
.badge{display:inline-flex;align-items:center;gap:8px;background:var(--chip);color:var(--brand-ink);border-radius:999px;padding:7px 15px;font-size:.84rem;font-weight:600;margin-bottom:20px}
.badge svg{font-size:1rem}
.hero h1{font-size:clamp(2.1rem,7.4vw,3.4rem);font-weight:800}
.hero h1 .hl{color:var(--brand-ink)}
.lede{margin-top:18px;font-size:1.08rem;color:var(--muted);max-width:560px}
.perks{list-style:none;margin:26px 0 0;padding:0;display:grid;gap:11px}
.perks li{display:flex;align-items:flex-start;gap:10px;font-size:.98rem}
.perks svg{font-size:1.2rem;color:var(--brand-ink);margin-top:.12em}
.hero-ctas{display:flex;flex-wrap:wrap;gap:12px;margin-top:30px}

.hero-media{position:relative}
.frame{background:var(--chip);border-radius:var(--r-lg);padding:12px;box-shadow:var(--shadow-lg)}
.frame img{border-radius:calc(var(--r-lg) - 6px);width:100%;aspect-ratio:4/3;object-fit:cover}
.float-card{display:flex;align-items:center;gap:12px;background:#fff;border-radius:var(--r);box-shadow:var(--shadow-lg);padding:13px 17px;margin:-34px 14px 0;position:relative}
.float-card .chip{margin:0}
.float-card b{display:block;font-size:.97rem;line-height:1.3}
.float-card small{color:var(--muted);font-size:.8rem}

/* ---------- blocos de seção ---------- */
.section{padding:64px 0}
.section--soft{background:var(--surface)}
.eyebrow{font-size:.79rem;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:var(--brand-ink);margin-bottom:12px}
.section-title{font-size:clamp(1.7rem,4.6vw,2.5rem);font-weight:700}
.section-lede{margin-top:16px;color:var(--muted);font-size:1.05rem}
.section-head{max-width:520px;margin-bottom:40px}
.section-head--center{text-align:center;margin-inline:auto;max-width:760px}
.prose{display:grid;gap:16px;color:var(--muted);font-size:1.05rem;max-width:660px;margin-inline:auto;text-align:center}

/* chip de ícone */
.chip{display:inline-flex;align-items:center;justify-content:center;width:48px;height:48px;border-radius:14px;background:var(--chip);color:var(--brand-ink);font-size:1.5rem}
.chip--sm{width:36px;height:36px;border-radius:10px;font-size:1.15rem}
.chip--wa{background:${WA_CHIP};color:var(--wa-dark)}

/* ---------- serviços ---------- */
.svc-grid{display:grid;grid-template-columns:1fr;gap:18px}
.svc{background:#fff;border:1px solid var(--line);border-radius:var(--r-lg);padding:26px;box-shadow:var(--shadow)}
.svc h3{margin:18px 0 7px;font-size:1.12rem;font-weight:700}
.svc p{color:var(--muted);font-size:.96rem}

/* ---------- por que escolher ---------- */
.why{display:grid;grid-template-columns:1fr;gap:34px;align-items:center}
.why-grid{display:grid;grid-template-columns:1fr;gap:14px}
.why-item{display:flex;align-items:center;gap:14px;background:#fff;border:1px solid var(--line);border-radius:var(--r);padding:16px 18px;font-size:.96rem;font-weight:500;box-shadow:var(--shadow)}

/* ---------- CTA do meio ---------- */
.cta-mid{text-align:center}
.cta-mid .btn{margin-top:28px}

/* ---------- nossa loja ---------- */
.store{display:grid;grid-template-columns:1fr;gap:26px;align-items:start}
.photo-card{position:relative;border-radius:var(--r-lg);overflow:hidden;box-shadow:var(--shadow-lg);background:var(--chip)}
.photo-card img{width:100%;aspect-ratio:4/5;object-fit:cover}
.photo-overlay{position:absolute;inset:auto 0 0 0;padding:26px 24px 22px;color:#fff;background:linear-gradient(transparent,rgba(0,0,0,.78))}
.photo-overlay .tag{display:inline-flex;align-items:center;gap:6px;background:rgba(255,255,255,.22);backdrop-filter:blur(4px);border-radius:999px;padding:5px 12px;font-size:.8rem;font-weight:500;margin-bottom:12px}
.photo-overlay b{display:block;font-family:var(--display);font-size:1.25rem;font-weight:700}
.photo-overlay small{opacity:.85;font-size:.87rem}
.map{width:100%;aspect-ratio:4/5;border:0;border-radius:var(--r-lg);background:var(--chip);box-shadow:var(--shadow-lg)}
.store-cards{display:grid;gap:16px}
.info-card{background:#fff;border:1px solid var(--line);border-radius:var(--r-lg);padding:24px;box-shadow:var(--shadow)}
.info-card .eyebrow{margin:18px 0 8px}
.info-card address{font-style:normal;color:var(--muted);font-size:1rem;line-height:1.55}
.info-card .big{font-family:var(--display);font-size:clamp(1.5rem,4.6vw,1.9rem);font-weight:700;color:var(--brand-ink);letter-spacing:-.02em;line-height:1.2}
.info-card .big--wa{color:var(--wa-dark)}
.info-card small{display:block;color:var(--muted);font-size:.87rem;margin-top:4px}
.info-card b{font-size:.98rem}
.info-link{display:inline-flex;align-items:center;gap:6px;margin-top:14px;color:var(--brand-ink);font-weight:600;font-size:.94rem;text-decoration:none}
.info-link:hover{text-decoration:underline}
.info-link svg{font-size:.95rem}

.hours{width:100%;border-collapse:collapse;margin-top:8px}
.hours th,.hours td{text-align:left;padding:9px 2px;border-bottom:1px solid var(--line);font-size:.94rem;font-weight:500}
.hours th{color:var(--muted);font-weight:500}
.hours td{text-align:right;font-variant-numeric:tabular-nums}
.hours tr:last-child th,.hours tr:last-child td{border-bottom:0}
.hours .today th,.hours .today td{color:var(--brand-ink);font-weight:700}
.hours .fechado td{color:var(--muted);font-weight:500}
.hours .today.fechado td{color:var(--muted)}

/* ---------- banner CTA ---------- */
.cta-banner{background:linear-gradient(140deg,var(--brand-ink) 0%,var(--brand-deep) 100%);color:#fff;border-radius:var(--r-lg);padding:56px 28px;text-align:center;box-shadow:var(--shadow-lg)}
.cta-banner h2{font-size:clamp(1.6rem,4.4vw,2.3rem);font-weight:700;max-width:660px;margin-inline:auto}
.cta-banner p{margin-top:14px;opacity:.9;max-width:540px;margin-inline:auto}
.cta-banner .btn{margin-top:28px}

/* ---------- rodapé ---------- */
.site-footer{background:var(--surface);border-top:1px solid var(--line);padding:56px 0 30px;font-size:.94rem;color:var(--muted)}
.footer-grid{display:grid;grid-template-columns:1fr;gap:34px}
.footer-grid h3{font-size:.79rem;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:var(--ink);margin-bottom:14px}
.footer-brand img{max-height:40px;width:auto;margin-bottom:14px}
.footer-brand .name{font-family:var(--display);font-weight:700;font-size:1.1rem;color:var(--ink);margin-bottom:10px}
.footer-list{list-style:none;margin:0;padding:0;display:grid;gap:10px}
.footer-list li{display:flex;align-items:flex-start;gap:10px}
.footer-list svg{font-size:1.05rem;color:var(--brand-ink);margin-top:.15em}
.footer-list a{color:inherit;text-decoration:none}
.footer-list a:hover{color:var(--brand-ink);text-decoration:underline}
.footer-company p{margin-bottom:6px}
.footer-company .rt{margin-top:14px}
.footer-company .rt span{display:block;font-size:.78rem;font-weight:700;letter-spacing:.13em;text-transform:uppercase;color:var(--brand-ink)}
.footer-company .rt-item{margin-top:6px}
.footer-company .rt b{color:var(--ink);font-size:.98rem}
.footer-legal{margin-top:38px;padding-top:26px;border-top:1px solid var(--line);display:grid;gap:9px;font-size:.83rem;line-height:1.55}
.footer-by{margin-top:26px;padding-top:20px;border-top:1px solid var(--line);text-align:center;font-size:.8rem}
.footer-by a{color:var(--muted)}

/* ---------- FAB ---------- */
.fab{position:fixed;right:18px;bottom:18px;z-index:50;background:var(--wa);color:#fff;width:58px;height:58px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 10px 28px rgba(3,161,74,.4);text-decoration:none}
.fab svg{font-size:1.9rem}
.fab:hover{background:var(--wa-dark)}

/* ---------- responsivo ---------- */
@media (min-width:640px){
  .svc-grid{grid-template-columns:1fr 1fr}
  .why-grid{grid-template-columns:1fr 1fr}
  .perks{grid-template-columns:1fr 1fr;column-gap:22px}
}
@media (min-width:900px){
  body{font-size:17px}
  .nav{display:flex}
  .site-header .btn{display:inline-flex}
  .hero{padding:64px 0 24px}
  .hero-grid{grid-template-columns:1.05fr .95fr;gap:56px}
  .hero-grid--solo{grid-template-columns:1fr;max-width:50rem}
  .hero-media{padding-left:14px}
  .frame{transform:rotate(1.4deg)}
  .frame img{aspect-ratio:5/4}
  .float-card{position:absolute;left:-14px;bottom:26px;margin:0;max-width:300px}
  .section{padding:88px 0}
  .svc-grid{grid-template-columns:repeat(3,1fr)}
  .why{grid-template-columns:1fr 1fr;gap:56px}
  .store{grid-template-columns:1.1fr .9fr;gap:40px}
  .cta-banner{padding:72px 40px}
  .footer-grid{grid-template-columns:1.2fr 1fr 1fr;gap:48px}
}
@media (prefers-reduced-motion:reduce){
  html{scroll-behavior:auto}
  *{transition:none!important}
  .frame{transform:none}
}
@media print{
  .site-header,.fab,.cta-banner,.map,.hero-media,.btn{display:none!important}
  body{font-size:11pt;background:#fff}
  .section{padding:16px 0}
}
</style>`;
}
//# sourceMappingURL=styles.js.map